import React, { useMemo } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Renders the seeker's palm photo with the AI-detected lines overlaid as
 * coloured SVG polylines + a legend strip + the AI reading text below.
 *
 * Expects payload shaped per palm_engine.process_palm_upload():
 *   { original_url, annotated_url, width, height,
 *     is_palm, is_clear, needs_close_up: [],
 *     lines: { heart_line:{points,quality}, head_line:{...}, ...,
 *              marriage_lines: [{point_start,point_end}, ...] },
 *     mounts: { jupiter:{strength}, ... },
 *     ai_reading: "...", line_legend: [{key,label,color:[r,g,b]}] }
 */
const LINE_KEYS = [
  "life_line", "head_line", "heart_line",
  "fate_line", "sun_line", "mercury_line",
];
const PRETTY = {
  life_line: "Life", head_line: "Head", heart_line: "Heart",
  fate_line: "Fate", sun_line: "Sun", mercury_line: "Mercury",
  marriage_lines: "Marriage",
};
const DEFAULT_COLOR = {
  life_line: "#059669", head_line: "#2563EB", heart_line: "#DC2626",
  fate_line: "#9333EA", sun_line: "#EA580C", mercury_line: "#0891B2",
  marriage_lines: "#EC4899",
};

const rgbToHex = (rgb) =>
  Array.isArray(rgb) && rgb.length >= 3
    ? "#" + rgb.slice(0, 3).map((n) => n.toString(16).padStart(2, "0")).join("")
    : null;

const Legend = ({ legend, lines }) => {
  const items = (legend || []).filter((l) => {
    const info = lines?.[l.key];
    if (l.key === "marriage_lines") return Array.isArray(info) && info.length > 0;
    return info && Array.isArray(info.points) && info.points.length >= 2;
  });
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {items.map((it) => (
        <span
          key={it.key}
          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-bg"
          style={{ borderColor: rgbToHex(it.color) || "#999", color: rgbToHex(it.color) || "#333" }}
          data-testid={`palm-legend-${it.key}`}
        >
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: rgbToHex(it.color) || "#999" }}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
};

const PalmAnalysisMessage = ({ payload, text }) => {
  const p = payload || {};
  const lines = p.lines || {};
  const ow = p.width || 1024;
  const oh = p.height || 1024;
  const imgSrc = (p.original_url || "").startsWith("http") ? p.original_url : `${BACKEND}${p.original_url || ""}`;

  // Build SVG polyline elements (we draw the overlay client-side so the SVG
  // is selectable and crisp, instead of relying solely on the PNG file).
  const polylines = useMemo(() => {
    const out = [];
    LINE_KEYS.forEach((k) => {
      const info = lines[k] || {};
      const pts = info.points || [];
      if (pts.length < 2) return;
      const d = pts
        .map(([x, y]) => `${Number(x).toFixed(1)},${Number(y).toFixed(1)}`)
        .join(" ");
      out.push({ key: k, d, color: DEFAULT_COLOR[k] });
    });
    (lines.marriage_lines || []).forEach((m, i) => {
      const ps = m.point_start || m.start;
      const pe = m.point_end || m.end;
      if (!ps || !pe) return;
      out.push({
        key: `marriage_${i}`,
        d: `${ps[0]},${ps[1]} ${pe[0]},${pe[1]}`,
        color: DEFAULT_COLOR.marriage_lines,
      });
    });
    return out;
  }, [lines]);

  const strokeW = Math.max(3, Math.round(ow / 220));

  return (
    <div
      className="max-w-[95%] sm:max-w-[88%] bg-[#FFF9E6] border border-[#D4AF37]/60 rounded-2xl rounded-bl-sm px-3 sm:px-4 py-3 shadow-sm"
      data-testid="palm-analysis-card"
    >
      <p className="label-eyebrow !text-[#7A5A12] !text-[10px]">
        Hasta Rekha · palm-line analysis
      </p>

      {/* Status banner if photo isn't usable */}
      {(p.is_palm === false || p.is_clear === false) && (
        <div className="mt-2 mb-2 text-xs text-[#7C2D12] bg-[#FFE4B5] border border-[#FBBF24]/60 px-2 py-1.5 rounded-md">
          {p.is_palm === false
            ? "I'm not sure that's a palm — could you re-share a photo of your dominant palm?"
            : "The photo isn't sharp enough for a full reading. Could you re-shoot in better light?"}
          {p.needs_close_up?.length ? (
            <div className="mt-1 text-[11px]">
              Close-ups needed: <b>{p.needs_close_up.join(", ")}</b>
            </div>
          ) : null}
        </div>
      )}

      {/* Image + SVG overlay */}
      {imgSrc && (
        <div
          className="relative w-full mt-2 rounded-lg overflow-hidden bg-black/5"
          style={{ aspectRatio: `${ow} / ${oh}` }}
          data-testid="palm-image-wrap"
        >
          <img
            src={imgSrc}
            alt="palm"
            className="absolute inset-0 w-full h-full object-contain"
            loading="lazy"
            data-testid="palm-original-img"
          />
          <svg
            viewBox={`0 0 ${ow} ${oh}`}
            preserveAspectRatio="xMidYMid meet"
            className="absolute inset-0 w-full h-full pointer-events-none"
            data-testid="palm-overlay-svg"
          >
            {polylines.map((pl) => (
              <polyline
                key={pl.key}
                points={pl.d}
                fill="none"
                stroke={pl.color}
                strokeWidth={strokeW}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ filter: "drop-shadow(0 0 2px rgba(0,0,0,0.4))" }}
              />
            ))}
          </svg>
        </div>
      )}

      <Legend legend={p.line_legend} lines={lines} />

      {/* AI reading text */}
      {(text || p.ai_reading) && (
        <div className="mt-3 text-xs sm:text-sm text-[#3F2D1B] leading-relaxed whitespace-pre-wrap">
          {text || p.ai_reading}
        </div>
      )}
    </div>
  );
};

export default PalmAnalysisMessage;
